import React, { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Stack,
  Text,
} from '@chakra-ui/react'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

import { docOptions } from '@/features/offers/constants/options'
import {
  hidePrintOfferModal,
  setPrintCaptureTargetCostBudget,
  setPrintCaptureTargetGCPRSubmission,
  setPrintCaptureTargetPRVendorSelection,
  setPrintCaptureTargetTab,
  startPrintCapture,
  stopPrintCapture,
} from '@/stores/offers/offersSlice'
import { selectPrintOfferModal } from '@/stores/offers/printOfferModal'
import { useDispatch, useSelector } from 'react-redux'
import useOffer from '@/features/offers/hooks/useOffer'
import { OfferDocumentTypes } from '@/features/offers/constants'
import config from '@/config'
import { addHeaderToCanvas, captureElementAsCanvas } from '@/features/offers/helpers/pdfCapture.helper'
import { createPdfFromCapturedTabs } from '@/features/offers/helpers/pdfAssembly.helper'
import {
  getOfferFormTabIndex,
  GCPR_TAB_INDEX,
  SOURCING_TAB_INDEX,
} from '@/features/offers/helpers/printTabIndex.helper'
import {
  filterPrintDocOptions,
  getPrintTabOptions,
  getSelectableTabValues,
} from '@/features/offers/helpers/printOptions.helper'
import { selectOfferDetail } from '@/features/offers/slices/offerDetail.slice'

const PR_TAB_INDEX = getOfferFormTabIndex('pr')

const CAPTURE_READY_TIMEOUT_MS = 8000
const CAPTURE_READY_POLL_INTERVAL_MS = 100

// tabOptions groups Sourcing's General/PM040/PM100 checkboxes under one parent even though they
// all capture the same physical tab — so a checked child's label falls back to its parent's.
const getTabLabelForIndex = (tabIndex, tabOptions) => {
  for (const tab of tabOptions) {
    if (Array.isArray(tab.children) && tab.children.length > 0) {
      if (tab.children.some((child) => getOfferFormTabIndex(child.value) === tabIndex)) {
        return tab.label
      }
    } else if (getOfferFormTabIndex(tab.value) === tabIndex) {
      return tab.label
    }
  }
  return `Tab ${tabIndex}`
}

// Chakra assigns each TabPanel a DOM id of the form `{tabsId}--tabpanel-{index}` (see
// makeTabPanelId in @chakra-ui/react's tabs/use-tabs.cjs), where index is its position among
// TabPanels' children — the same numbering getOfferFormTabIndex targets. Used to confirm the
// "not hidden" panel we found is actually the one OfferTabs just switched to, not a stale one.
const getPanelIndexFromId = (panel) => {
  const match = panel?.id?.match(/--tabpanel-(\d+)$/)
  return match ? Number(match[1]) : null
}

// Waits for a specific TabPanel (identified by its position among containerSelector's direct
// children) to have mounted content, rendered with a non-zero size, and finished any of its own
// data loading (tabs signal this with data-print-loading). Rejects rather than silently capturing
// a blank/zero-size/stale panel. Shared by the outer OfferTabs strip and CostBudgetTabs' nested
// tab strip — both are driven the same way (a Redux-published target index) and need the same
// "did it actually switch, and is it actually rendered" verification before capture.
//
// Measures with getBoundingClientRect (not offsetWidth/offsetHeight) after letting paint settle
// on every poll cycle — offsetWidth/offsetHeight report layout-box size and ignore CSS transforms,
// so a panel mid-transition (e.g. scaling in) can pass an offsetWidth check while still measuring
// zero via getBoundingClientRect, which is what html2canvas actually uses to size the capture.
const waitForPanelReady = ({ containerSelector, targetIndex, label }) => {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now()

    const checkAndSettle = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const panel = document.querySelector(`${containerSelector} > [role="tabpanel"]:not([hidden])`)
          const rect = panel?.getBoundingClientRect()
          const hasRenderedSize = !!rect && rect.width > 0 && rect.height > 0
          const isStillLoading = panel?.querySelector('[data-print-loading="true"]')
          const isTargetPanel = getPanelIndexFromId(panel) === targetIndex

          if (panel && isTargetPanel && hasRenderedSize && panel.childElementCount > 0 && !isStillLoading) {
            resolve(panel)
            return
          }

          if (Date.now() - startedAt > CAPTURE_READY_TIMEOUT_MS) {
            if (panel && isTargetPanel && hasRenderedSize) {
              // Best-effort: this tab's own data fetch may still be loading, but it has rendered
              // content with real dimensions, so it's safe to capture rather than fail the export.
              resolve(panel)
            } else {
              reject(
                new Error(
                  `${label} ${targetIndex}: expected panel index ${targetIndex}, found ${
                    panel ? getPanelIndexFromId(panel) : 'no panel'
                  } (size ${panel ? `${Math.round(rect.width)}x${Math.round(rect.height)}` : 'n/a'})`
                )
              )
            }
            return
          }

          setTimeout(checkAndSettle, CAPTURE_READY_POLL_INTERVAL_MS)
        })
      })
    }

    checkAndSettle()
  })
}

const waitForActiveTabPanelReady = (tabIndex) =>
  waitForPanelReady({ containerSelector: '#offerFormTabPanels', targetIndex: tabIndex, label: 'Offer tab' })

const waitForCostBudgetPanelReady = (costBudgetIndex) =>
  waitForPanelReady({
    containerSelector: '#offerSourcingCostBudgetTabPanels',
    targetIndex: costBudgetIndex,
    label: 'Cost budget',
  })

const waitForGCPRSubmissionPanelReady = (submissionIndex) =>
  waitForPanelReady({
    containerSelector: '#offerGCPRSubmissionTabPanels',
    targetIndex: submissionIndex,
    label: 'GCPR submission',
  })

const waitForPRVendorSelectionPanelReady = (vendorSelectionIndex) =>
  waitForPanelReady({
    containerSelector: '#offerPRVendorSelectionTabPanels',
    targetIndex: vendorSelectionIndex,
    label: 'PR vendor selection',
  })

const TabOptionGroup = ({ tab, setCheckedTabs, checkedTabs }) => {
  if (Array.isArray(tab.children) && tab.children.length > 0) {
    const childValues = tab.children.map((child) => child.value)
    const checkedChildrenCount = childValues.filter((val) => checkedTabs.includes(val)).length
    const allChecked = checkedChildrenCount === childValues.length
    const isIndeterminate = checkedChildrenCount > 0 && !allChecked

    return (
      <>
        <Checkbox
          key={tab.value}
          isChecked={allChecked}
          isIndeterminate={isIndeterminate}
          onChange={() => {
            if (allChecked) {
              // Remove all children
              setCheckedTabs((prev) => prev.filter((t) => !childValues.includes(t)))
            } else {
              // Add all children
              setCheckedTabs((prev) => [...new Set([...prev, ...childValues])])
            }
          }}
        >
          {tab.label}
        </Checkbox>
        <Stack pl={6} mt={1} spacing={1}>
          {tab.children.map((childTab) => (
            <Checkbox
              key={childTab.value}
              value={childTab.value}
              isChecked={checkedTabs.includes(childTab.value)}
              onChange={(e) => {
                if (e.target.checked) {
                  setCheckedTabs((prev) => [...prev, childTab.value])
                } else {
                  setCheckedTabs((prev) => prev.filter((t) => t !== childTab.value))
                }
              }}
            >
              {childTab.label}
            </Checkbox>
          ))}
        </Stack>
      </>
    )
  }

  return (
    <Checkbox
      key={tab.value}
      value={tab.value}
      isChecked={checkedTabs.includes(tab.value)}
      onChange={(e) => {
        if (e.target.checked) {
          setCheckedTabs((prev) => [...prev, tab.value])
        } else {
          setCheckedTabs((prev) => prev.filter((t) => t !== tab.value))
        }
      }}
    >
      {tab.label}
    </Checkbox>
  )
}

const PrintModal = () => {
  const { isOpened, subSystemNames, offerId } = useSelector(selectPrintOfferModal)
  const { role } = useSelector(selectOfferDetail)

  const [checkedTabs, setCheckedTabs] = useState([])
  const [otherDocs, setOtherDocs] = useState([])

  const [checkedDocs, setCheckedDocs] = useState(docOptions.map((option) => option.value))
  const [captureProgress, setCaptureProgress] = useState(null)

  const dispatch = useDispatch()
  const { useGetMergedDocument, useFetchOffer } = useOffer()
  const getMergedDocument = useGetMergedDocument()

  // Some tabs have their own nested tab strip (Sourcing's cost budgets, GCPR's submissions, PR's
  // vendor selections) — only whichever nested item happens to already be selected is visible at
  // once, so capture the panel once per nested item instead of once overall. If there's no nested
  // strip (count 0), falls back to a single capture of the panel as-is.
  const captureViaNestedTabs = async ({
    panelElement,
    label,
    containerSelector,
    waitFn,
    setTargetIndex,
    itemLabel,
  }) => {
    const count = document.querySelectorAll(`${containerSelector} > [role="tabpanel"]`).length

    if (count === 0) {
      return [addHeaderToCanvas(await captureElementAsCanvas(panelElement), label)]
    }

    const canvases = []
    try {
      for (let index = 0; index < count; index++) {
        dispatch(setTargetIndex(index))
        await waitFn(index)
        const canvas = await captureElementAsCanvas(panelElement)
        canvases.push(addHeaderToCanvas(canvas, `${label} — ${itemLabel} ${index + 1}`))
      }
    } finally {
      dispatch(setTargetIndex(null))
    }
    return canvases
  }

  // Switches OfferForm through each selected tab and captures it as an image, in the modal's list
  // order (not on-screen tab order). Returns undefined if no tabs are selected.
  const captureCheckedTabsToPdfFile = async () => {
    const orderedTabIndices = [...new Set(selectedTabs.map(getOfferFormTabIndex))].filter(
      (tabIndex) => tabIndex !== null
    )

    if (orderedTabIndices.length === 0) {
      return undefined
    }

    dispatch(startPrintCapture())

    try {
      const canvases = []
      for (let i = 0; i < orderedTabIndices.length; i++) {
        setCaptureProgress({ current: i + 1, total: orderedTabIndices.length })
        dispatch(setPrintCaptureTargetTab(orderedTabIndices[i]))
        const panelElement = await waitForActiveTabPanelReady(orderedTabIndices[i])
        const label = getTabLabelForIndex(orderedTabIndices[i], tabOptions)

        try {
          let capturedCanvases
          if (orderedTabIndices[i] === SOURCING_TAB_INDEX) {
            // Capture the whole Sourcing panel (not just the nested cost-budget panel) once per
            // cost budget, so each page still includes the Sourcing general info for context.
            capturedCanvases = await captureViaNestedTabs({
              panelElement,
              label,
              containerSelector: '#offerSourcingCostBudgetTabPanels',
              waitFn: waitForCostBudgetPanelReady,
              setTargetIndex: setPrintCaptureTargetCostBudget,
              itemLabel: 'Cost Budget',
            })
          } else if (orderedTabIndices[i] === GCPR_TAB_INDEX) {
            capturedCanvases = await captureViaNestedTabs({
              panelElement,
              label,
              containerSelector: '#offerGCPRSubmissionTabPanels',
              waitFn: waitForGCPRSubmissionPanelReady,
              setTargetIndex: setPrintCaptureTargetGCPRSubmission,
              itemLabel: 'Submission',
            })
          } else if (PR_TAB_INDEX !== null && orderedTabIndices[i] === PR_TAB_INDEX) {
            capturedCanvases = await captureViaNestedTabs({
              panelElement,
              label,
              containerSelector: '#offerPRVendorSelectionTabPanels',
              waitFn: waitForPRVendorSelectionPanelReady,
              setTargetIndex: setPrintCaptureTargetPRVendorSelection,
              itemLabel: 'Vendor Selection',
            })
          } else {
            capturedCanvases = [addHeaderToCanvas(await captureElementAsCanvas(panelElement), label)]
          }
          canvases.push(...capturedCanvases)
        } catch (captureError) {
          throw new Error(`Offer tab ${orderedTabIndices[i]}: ${captureError.message}`)
        }
      }

      const pdfBlob = createPdfFromCapturedTabs(canvases)
      return new File([pdfBlob], 'offerSummary.pdf', { type: 'application/pdf' })
    } finally {
      dispatch(stopPrintCapture())
      setCaptureProgress(null)
    }
  }

  const { data: offer } = useFetchOffer(offerId)

  const getLatestFileId = (document) => {
    if (!document.files?.length) return undefined
    const maxVersion = Math.max(...document.files.map((f) => f.version ?? 0))
    return document.files.find((f) => f.version === maxVersion)?._id
  }

  // merged docs
  const docOptionsMerged = useMemo(() => {
    const externalOthers =
      offer?.documents?.externalOthers?.map((document, index) => {
        const latestFileVersion = getLatestFileId(document)
        return {
          value: `${OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS}_${index + 1}`,
          label: `1.${5 + index} ${document.name}`,
          fileId: latestFileVersion,
        }
      }) ?? []

    const internalOthers =
      offer?.documents?.internalOthers?.map((document, index) => {
        const latestFileVersion = getLatestFileId(document)
        return {
          value: `${OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS}_${index + 1}`,
          label: `2.${13 + index} ${document.name}`,
          fileId: latestFileVersion,
        }
      }) ?? []

    const merged = [...docOptions, ...externalOthers, ...internalOthers]

    const idxExternal = merged.findIndex((opt) => opt.value === OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS)
    const idxInternal = merged.findIndex((opt) => opt.value === OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS)

    let filtered = [...merged]

    if (idxExternal !== -1) {
      filtered.splice(idxExternal, 1)
    }

    if (idxInternal !== -1) {
      const newIdxInternal = filtered.findIndex((opt) => opt.value === OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS)
      if (newIdxInternal !== -1) {
        filtered.splice(newIdxInternal, 1)
      }
    }
    // otherDocs is array of fileId
    setOtherDocs([
      ...externalOthers.flatMap((opt) => (opt.fileId ? [opt.fileId] : [])),
      ...internalOthers.flatMap((opt) => (opt.fileId ? [opt.fileId] : [])),
    ])
    setCheckedDocs((prev) => {
      const withoutBaseDocs = prev.filter(
        (val) =>
          val !== OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS && val !== OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS
      )

      return [
        ...new Set([
          ...withoutBaseDocs,
          ...(externalOthers?.map((opt) => opt.value) ?? []),
          ...(internalOthers?.map((opt) => opt.value) ?? []),
        ]),
      ]
    })

    return filterPrintDocOptions(
      filtered.sort((a, b) => a.label.localeCompare(b.label)),
      role
    )
  }, [offer, role])

  // checkedDocs/checkedTabs can hold values the current role isn't offered (they default to every
  // option), so only what is actually listed is counted and sent.
  const selectedDocs = checkedDocs.filter((value) => docOptionsMerged.some((doc) => doc.value === value))
  const docAllChecked = selectedDocs.length === docOptionsMerged.length
  const docIsIndeterminate = selectedDocs.length > 0 && !docAllChecked

  const tabOptions = useMemo(() => getPrintTabOptions({ subSystemNames, role }), [subSystemNames, role])
  const allSelectableTabValues = useMemo(() => getSelectableTabValues(tabOptions), [tabOptions])

  const selectedTabs = allSelectableTabValues.filter((value) => checkedTabs.includes(value))
  const tabAllChecked = selectedTabs.length === allSelectableTabValues.length
  const tabIsIndeterminate = selectedTabs.length > 0 && !tabAllChecked

  // Handler

  const closeModal = () => {
    setCheckedTabs(allSelectableTabValues)
    setCheckedDocs(docOptionsMerged.map((option) => option?.value))

    dispatch(hidePrintOfferModal())
  }

  const sanitizeCheckedDocs = (docs) => {
    const result = new Set()

    docs.forEach((doc) => {
      if (doc.startsWith(OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS)) {
        result.add(OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS)
      } else if (doc.startsWith(OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS)) {
        result.add(OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS)
      } else {
        result.add(doc)
      }
    })

    return Array.from(result)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    // startPrint(checkedTabs, checkedDocs);

    //Get the links of the download file.

    try {
      dispatch(showLoadingOverlay(true))

      const sanitizedDocs = sanitizeCheckedDocs(selectedDocs)

      let offerSummaryFile
      if (config.enableClientPdfCapture) {
        offerSummaryFile = await captureCheckedTabsToPdfFile()
      }

      const response = await getMergedDocument.mutateAsync({
        offerId,
        // When we've already captured the tabs client-side, offerSummaryFile carries that
        // content and the server should skip its own Puppeteer render.
        tabs: offerSummaryFile ? [] : selectedTabs,
        fileTypes: sanitizedDocs,
        otherDocs: otherDocs,
        offerSummaryFile,
      })
      const blob = new Blob([response], {
        type: 'application/pdf',
      })
      if (blob) {
        const fileUrl = URL.createObjectURL(blob)

        window.open(fileUrl)
      }
      closeModal()
    } catch (error) {
      toast.error(`Failed to generate print document: ${error.message}`)
      return
    } finally {
      dispatch(hideLoadingOverlay(false))
    }
  }

  return (
    <Modal isOpen={isOpened} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent maxW={'60%'}>
        <form onSubmit={onSubmit}>
          <ModalHeader>Print Offer</ModalHeader>

          <ModalCloseButton />
          <ModalBody>
            {captureProgress && (
              <Box mb="4">
                <Text fontSize="sm" mb="1">
                  Capturing tab {captureProgress.current} of {captureProgress.total}…
                </Text>
                <Progress
                  value={(captureProgress.current / captureProgress.total) * 100}
                  size="sm"
                  colorScheme="teal"
                />
              </Box>
            )}
            <Flex gap="2">
              <Box mb="2">
                <Checkbox
                  isChecked={tabAllChecked}
                  isIndeterminate={tabIsIndeterminate}
                  onChange={() => {
                    if (tabAllChecked) {
                      setCheckedTabs([])
                    } else {
                      setCheckedTabs(allSelectableTabValues)
                    }
                  }}
                >
                  Select Tabs
                </Checkbox>
                <CheckboxGroup colorScheme="blue" defaultValue={checkedTabs} value={checkedTabs}>
                  <Stack pl={6} mt={1} spacing={1}>
                    {tabOptions.map((tab) => (
                      <TabOptionGroup
                        key={tab.value}
                        tab={tab}
                        setCheckedTabs={setCheckedTabs}
                        checkedTabs={checkedTabs}
                      />
                    ))}
                  </Stack>
                </CheckboxGroup>
              </Box>
              <Box>
                <Checkbox
                  isChecked={docAllChecked}
                  isIndeterminate={docIsIndeterminate}
                  onChange={() => {
                    if (docAllChecked) {
                      setCheckedDocs([])
                    } else {
                      setCheckedDocs(docOptionsMerged.map((tab) => tab.value))
                    }
                  }}
                >
                  Select Documents
                </Checkbox>
                <CheckboxGroup colorScheme="blue" defaultValue={checkedDocs} value={checkedDocs}>
                  <Stack pl={6} mt={1} spacing={1}>
                    {docOptionsMerged.map((doc, index) => (
                      <Checkbox
                        key={doc.value + index}
                        value={doc.value}
                        onChange={(e) => {
                          if (e.target.checked) {
                            if (
                              doc.value.startsWith(
                                OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS ||
                                  OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS
                              )
                            ) {
                              setOtherDocs((prev) => [...prev, doc.fileId])
                            }
                            setCheckedDocs((prev) => [...prev, doc.value])
                          } else {
                            if (
                              doc.value.startsWith(
                                OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS ||
                                  OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS
                              )
                            ) {
                              setOtherDocs((prev) => prev.filter((d) => d !== doc.fileId))
                            }
                            setCheckedDocs((prev) => prev.filter((d) => d !== doc.value))
                          }
                        }}
                      >
                        {doc.label}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              </Box>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <HStack>
              <Button colorScheme="green" type="submit" isLoading={!!captureProgress} loadingText="Capturing...">
                Confirm
              </Button>

              <Button colorScheme="gray" onClick={closeModal} isDisabled={!!captureProgress}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

export default PrintModal
